import { computed, Directive, effect, inject, input, signal, Signal, viewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TDeliverableForm } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { IManualDeliverableStatusConfig } from '@cta-analytics-details/cta-analytics-details-deliverable-card/manual-deliverable-status/manual-devlierable-status.interface';
import {
  IKeywordCard,
  IKpiCard,
} from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { ICtaAnalyticsData } from '@cta-analytics-dialog/cta-analytics-dialog-content/cta-analytics-table/cta-analytics-table.interface';
import { DeliverableFindingsService } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.service';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { FindingDeleteService } from '@cta-analytics-dialog/deliverable-analytics-content/finding-delete.service';
import { ITimelineGraphInputData } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/timeline-graph/timeline-graph.interface';
import { ManualFindingStatusStore } from '@cta-analytics-dialog/manual-finding-status.store';
import { EAnalyticsStatus, EMatchingType, EProcessStatus } from '@enums/campaign-analytics.enum';
import { EDeliverableType, EPlatform, EPlatformFeature } from '@enums/campaign.enum';
import { EEventType } from '@enums/event-tracking.enum';
import {
  IBaseDeliverableAnalyticsApi,
  ISocialPostApi,
  IYoutubeVodApi,
} from '@interfaces/campaign-analytics-api.interface';
import { IFinding, IKeywordHolder } from '@interfaces/deliverable-analytics.interface';
import { IFindingVisualizer } from '@interfaces/finding-visualizer.interface';
import { TranslateService } from '@ngx-translate/core';
import { IVideoPlayedContext, IVideoPlayerConfiguration } from '@shared/components/video-player/video-player.interface';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { DISPLAY_TIME_FORMAT } from '@utilities/date-utilities.service';
import { MediaUtilitiesService } from '@utilities/media-utilities.service';
import { EChartsOption } from 'echarts';

@Directive({
  providers: [DeliverableFindingsStore],
  selector: '[appMultiMediaPostAnalyticsBase]',
})
export abstract class MultiMediaPostAnalyticsBaseDirective<
  TableData extends {
    id: string;
    mediaIndex?: number;
    timestamp?: string | null;
    videoTime?: number;
  },
  Deliverable extends IBaseDeliverableAnalyticsApi,
> {
  public readonly deliverableForm = input.required<FormGroup<TDeliverableForm>>();
  public readonly deliverableId = input.required<string | null>();
  public readonly executionAnalytics = input.required<ICtaAnalyticsData | null>();
  public readonly manualDeliverableStatusConfig = input.required<IManualDeliverableStatusConfig>();

  public abstract readonly displayedColumns: string[];
  public abstract readonly dataSource: MatTableDataSource<TableData>;
  public abstract readonly kpiCardData: IKpiCard | null;
  public abstract readonly deliverableType: EDeliverableType;

  public readonly sort = viewChild<MatSort>(MatSort);

  public readonly status = signal<EAnalyticsStatus>(EAnalyticsStatus.processing);
  public readonly seekTime = signal<number | null>(null);
  public readonly dataCount = signal<number | null>(null);
  public readonly mediaDurationsSeconds = signal<number[]>([]);
  public readonly mediaDurationsReady = signal<boolean>(false);
  public readonly chartConfiguration = signal<Partial<EChartsOption>>({});

  public readonly keywordList = signal<IKeywordCard[]>([]);
  public readonly keywordCardList = signal<IKpiCard[]>([]);

  public readonly dotGraphData = signal<ITimelineGraphInputData[]>([]);
  public readonly keywordTableRowIds = signal<Record<string, string[]>>({});
  public readonly keywordTableRowIdsReady = signal<boolean>(false);
  public readonly selectedDataId = signal<string | null>(null);

  public readonly findingsLoaded = signal<boolean>(false);

  public readonly executionAnalyticsWithFindings = computed(() => {
    const analytics = this.executionAnalytics();
    const findings = this.findingsStore.findings$();

    if (!analytics || !this.findingsLoaded()) {
      return analytics;
    }

    return this.findingsService.mergeFindingsIntoAnalytics(
      analytics,
      findings,
      this.deliverableId()!,
      this.deliverableType,
    );
  });

  public readonly allKpiCards = computed(() => [this.kpiCardData, ...this.keywordCardList()]);
  public readonly youtubeOriginalUrl = computed<string | undefined>(() => {
    const socialContent = this.executionAnalyticsWithFindings()?.content?.socialContent;

    return (socialContent as IYoutubeVodApi)?.youtubeData?.originalUrl;
  });
  public readonly totalMediaDurationSeconds = computed(() => {
    const total = this.mediaDurationsSeconds().reduce((sum, d) => sum + d, 0);

    return total > 0 ? total : null;
  });
  public readonly media = computed(
    () => (this.executionAnalyticsWithFindings()?.content?.socialContent as ISocialPostApi)?.media ?? [],
  );
  public readonly platformFeature = computed<EPlatformFeature | null>(() => {
    return this.executionAnalyticsWithFindings()?.platformFeature ?? this.executionAnalytics()?.platformFeature ?? null;
  });
  public readonly shouldRenderInPagePlayer = computed(() => {
    return !this.usesExternalVideoLink();
  });
  public readonly usesExternalVideoLink = computed(() => {
    return AnalyticsDetailsUtilitiesService.isExternalVideoLinkPlatformFeature(this.platformFeature());
  });

  public readonly EPlatformFeature = EPlatformFeature;
  public readonly EPlatform = EPlatform;
  public readonly EMatchingType = EMatchingType;

  public readonly analyticsStatusBadgeLoading = computed(() => {
    return !this.findingsLoaded();
  });

  protected abstract readonly currentDeliverable: Signal<Deliverable | null | undefined>;

  protected readonly displayTimeFormat = DISPLAY_TIME_FORMAT;
  protected readonly defaultVideoConfiguration: IVideoPlayerConfiguration = {
    autoPlay: false,
    src: '',
  } as const;
  public readonly videoConfiguration = signal<IVideoPlayerConfiguration>({
    ...this.defaultVideoConfiguration,
  });

  public readonly manualFindingStatusStore = inject(ManualFindingStatusStore, { optional: true });

  public readonly getKeywordCount = (keyword: string): number | null => {
    const removedIds = this.manualFindingStatusStore?.allRemovedIds() ?? new Set<string>();
    const keywords =
      (this.currentDeliverable() as unknown as IKeywordHolder<IFinding> | null | undefined)?.keywords ?? [];

    return AnalyticsDetailsUtilitiesService.computeKeywordCount(keyword, keywords, removedIds);
  };

  protected readonly translate = inject(TranslateService);
  protected readonly mediaUtils = inject(MediaUtilitiesService);
  protected readonly findingsStore = inject(DeliverableFindingsStore);
  protected readonly findingsService = inject(DeliverableFindingsService);
  private readonly findingDeleteService = inject(FindingDeleteService);
  private readonly eventTrackingService = inject(EventTrackingService);

  // Always rendered inside the CTA analytics popup — see event-tracking-implementation-map.md #33.
  protected readonly videoPlayedContext: IVideoPlayedContext = {
    analyticsPopupActive: true,
    eventType: EEventType.campaignAnalyticsContentVideoPlayed,
  };

  public readonly canDelete = computed(
    () => this.executionAnalytics()?.campaignProcessStatus === EProcessStatus.processing,
  );

  protected keywords: string[] = [];
  private lastFindingsRequestKey: string | null = null;
  private readonly keywordSelectionIndices = new Map<string, number>();

  constructor() {
    effect(() => {
      if (!this.manualDeliverableStatusConfig().analyticsVisible) {
        this.findingsLoaded.set(true);
        this.keywordTableRowIdsReady.set(true);
        this.lastFindingsRequestKey = null;

        return;
      }

      const deliverableId = this.deliverableId();
      const executionAnalytics = this.executionAnalytics();
      const socialContentId = executionAnalytics?.content?.socialContent?.uniqueExternalId;

      if (deliverableId && socialContentId) {
        const findingType = this.findingsService.mapDeliverableTypeToFindingType(this.deliverableType);

        if (findingType) {
          const findingsRequestKey = `${deliverableId}_${findingType}_${socialContentId}`;

          if (this.lastFindingsRequestKey === findingsRequestKey) {
            return;
          }

          this.lastFindingsRequestKey = findingsRequestKey;
          this.findingsLoaded.set(false);
          this.keywordTableRowIdsReady.set(false);
          this.findingsStore.fetchFindings({
            campaignExportId: this.manualDeliverableStatusConfig().exportMode
              ? this.manualDeliverableStatusConfig().campaignExportId
              : null,
            deliverableId,
            findingType,
            socialContentId,
          });
        }
      }
    });

    effect(() => {
      const loading = this.findingsStore.loading$();

      if (!loading) {
        this.findingsLoaded.set(true);
      }
    });

    this.createCountsAndStatusEffects();
    this.createPrepareChartAndTableDataEffect();
    this.createUpdateMediaDurationsEffect();
    this.createKeywordsEffect();
  }

  public createCountsAndStatusEffects(): void {
    effect(() => {
      if (this.executionAnalyticsWithFindings()) {
        this.setCounts();
        this.setStatus();
        this.setKeywordList();
      }
    });
  }

  public createPrepareChartAndTableDataEffect(): void {
    effect(() => {
      this.manualFindingStatusStore?.serverRemovedIds();

      if (this.executionAnalyticsWithFindings() && this.mediaDurationsReady()) {
        this.prepareChartAndTableData();

        if (this.dataSource.data.length > 0) {
          this.setVideoConfiguration(this.dataSource.data[0].mediaIndex, this.dataSource.data[0].videoTime);
        } else if (this.media().length > 0) {
          this.setVideoConfiguration(0, 0);
        }
      }
    });
  }

  public createUpdateMediaDurationsEffect(): void {
    effect(() => {
      const mediaList = this.media();

      if (mediaList && mediaList.length > 0) {
        this.setVideoConfiguration(0, 0);
        void this.updateMediaDurations();
      } else {
        this.mediaDurationsReady.set(true);
        this.mediaDurationsSeconds.set([]);
      }
    });
  }

  public createKeywordsEffect(): void {
    effect(() => {
      const deliverableId = this.deliverableId();
      const executionAnalytics = this.executionAnalyticsWithFindings();

      if (deliverableId && executionAnalytics) {
        this.keywords = this.getKeywords();
      }
    });
  }

  public highlightKeywords(text: string, rowKeywords?: string[]): string {
    return AnalyticsDetailsUtilitiesService.highlightKeywords(rowKeywords ?? this.keywords, text);
  }

  public onDataPointClick(clickedPointId: string): void {
    const clickedPoint = this.dataSource.data.find((data) => data.id === clickedPointId);

    if (!this.usesExternalVideoLink()) {
      this.setVideoConfiguration(clickedPoint?.mediaIndex, clickedPoint?.videoTime);
    }

    this.selectDataPoint(clickedPointId);
    this.trackFindingEntryClicked();
  }

  public onRowClick(event: Event, element: TableData): void {
    event.stopPropagation();

    if (this.usesExternalVideoLink()) {
      this.openTimestampedVideoUrl(element.mediaIndex, element.videoTime);
      this.trackFindingEntryClicked();

      return;
    }

    this.setVideoConfiguration(element.mediaIndex, element.videoTime);
    this.trackFindingEntryClicked();
  }

  private trackFindingEntryClicked(): void {
    this.eventTrackingService.track(EEventType.campaignAnalyticsFindingEntryClicked, {
      contentStatus: this.status(),
      deliverableId: this.deliverableId() ?? undefined,
      deliverableType: this.deliverableType,
      matchingType: this.executionAnalytics()?.status,
      socialContentId: this.executionAnalytics()?.socialContentId ?? undefined,
    });
  }

  public buildDeleteMenuItems(
    element: Pick<IFindingVisualizer, 'id' | 'findings' | 'keywordMappings'>,
  ): Array<{ label: string; findingId: string; keyword: string; keywordId: string }> {
    return this.findingDeleteService.buildDeleteMenuItems(element, this.manualFindingStatusStore);
  }

  public onDeleteFinding(
    findingId: string,
    element: Pick<IFindingVisualizer, 'id' | 'findings' | 'keywordMappings'>,
  ): void {
    if (!this.manualFindingStatusStore) return;

    const context = this.findingDeleteService.getDeleteContext(this.executionAnalytics(), this.deliverableId());

    if (!context) return;

    this.findingDeleteService.deleteFinding(findingId, element, this.manualFindingStatusStore, context);
  }

  public onDeleteAllFindings(element: Pick<IFindingVisualizer, 'id' | 'findings' | 'keywordMappings'>): void {
    if (!this.manualFindingStatusStore) return;

    const context = this.findingDeleteService.getDeleteContext(this.executionAnalytics(), this.deliverableId());

    if (!context) return;

    this.findingDeleteService.deleteAllFindings(element, this.manualFindingStatusStore, context);
  }

  public onRevertRow(element: Pick<IFindingVisualizer, 'id'>): void {
    this.findingDeleteService.revertRow(element, this.manualFindingStatusStore);
  }

  public hasRowPendingDeletions(element: Pick<IFindingVisualizer, 'id'>): boolean {
    return this.findingDeleteService.hasRowPendingDeletions(element, this.manualFindingStatusStore);
  }

  public hasRowActiveFinding(element: Pick<IFindingVisualizer, 'findings'>): boolean {
    return this.findingDeleteService.hasRowActiveFinding(element, this.manualFindingStatusStore);
  }

  public getActiveKeywords(element: Pick<IFindingVisualizer, 'findings' | 'keywordMappings'>): string[] {
    return this.findingDeleteService.getActiveKeywords(element, this.manualFindingStatusStore);
  }

  public isVisualizerActing(visualizerId: string): boolean {
    return this.findingDeleteService.isVisualizerActing(visualizerId, this.manualFindingStatusStore);
  }

  public isFindingInFlight(findingId: string): boolean {
    return this.findingDeleteService.isFindingInFlight(findingId, this.manualFindingStatusStore);
  }

  public onDeleteSingleRowFinding(element: { id: string; findingParentId: string }): void {
    if (!this.manualFindingStatusStore) return;

    const context = this.findingDeleteService.getDeleteContext(this.executionAnalytics(), this.deliverableId());

    if (!context) return;

    const findingType = this.findingsService.mapDeliverableTypeToFindingType(this.deliverableType);

    if (!findingType) return;

    this.findingDeleteService.deleteSingleRowFinding(element, findingType, this.manualFindingStatusStore, context);
  }

  protected abstract prepareChartAndTableData(): void;
  protected abstract setCounts(): void;
  protected abstract setKeywordList(): void;
  protected abstract getKeywords(): string[];

  protected setVideoConfiguration(mediaIndex: number | undefined, videoTime: number | undefined): void {
    this.videoConfiguration.set({
      ...this.defaultVideoConfiguration,
      src: this.media()[mediaIndex ?? 0].url,
    });
    this.seekTime.set(videoTime ?? 0);
  }

  protected setStatus(): void {
    this.status.set(this.currentDeliverable()?.status ?? EAnalyticsStatus.processing);
  }

  protected setKeywordTableRowIds(keywordTableRowIds: Record<string, string[]>): void {
    this.keywordSelectionIndices.clear();
    this.keywordTableRowIds.set(keywordTableRowIds);
    this.keywordTableRowIdsReady.set(true);
  }

  protected openTimestampedVideoUrl(mediaIndex: number | undefined, videoTime: number | undefined): void {
    const platformFeature = this.platformFeature();
    const baseUrl =
      platformFeature === EPlatformFeature.youtubeVod ? this.youtubeOriginalUrl() : this.media()[mediaIndex ?? 0]?.url;
    const videoUrl = AnalyticsDetailsUtilitiesService.buildTimestampedVideoUrl(platformFeature, videoTime, baseUrl);

    if (!videoUrl) {
      return;
    }

    window.open(videoUrl, '_blank', 'noopener,noreferrer');

    this.eventTrackingService.track(EEventType.campaignAnalyticsExternalSourceOpened, {
      contentStatus: this.status(),
      matchingType: this.executionAnalytics()?.status,
      socialContentId: this.executionAnalytics()?.socialContentId ?? undefined,
    });
  }

  protected selectDataPoint(clickedPointId: string): void {
    this.selectedDataId.set(null);

    setTimeout(() => {
      this.selectedDataId.set(clickedPointId);
    });
  }

  public readonly jumpToNextKeywordFinding = (keyword: string): void => {
    const rowIds = this.keywordTableRowIds()[keyword] ?? [];

    if (!rowIds.length) {
      return;
    }

    const nextIndex = this.keywordSelectionIndices.get(keyword) ?? 0;
    const nextRowId = rowIds[nextIndex];

    this.keywordSelectionIndices.set(keyword, (nextIndex + 1) % rowIds.length);
    this.onDataPointClick(nextRowId);
  };

  protected async updateMediaDurations(): Promise<void> {
    const mediaList = this.media();

    if (!mediaList || mediaList.length === 0) {
      this.mediaDurationsSeconds.set([]);

      return;
    }

    const durations = await this.mediaUtils.getDurationsForMedia(mediaList);
    this.mediaDurationsReady.set(true);
    this.mediaDurationsSeconds.set(durations);
  }

  protected calculateAggregatedVideoTime(mediaIndex: number | undefined, videoTime: number | undefined): number {
    if (mediaIndex === undefined) {
      return 0;
    }

    const videoTimeUpToIndex = this.mediaDurationsSeconds()
      .slice(0, mediaIndex)
      .reduce((sum, duration) => sum + duration, 0);

    return videoTimeUpToIndex + (videoTime ?? 0);
  }
}
